'use client';

import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useActionState, useEffect, useRef, useState } from 'react';

import { Alert } from '@hrworks/sui-core/Alert';
import { BlockUI } from '@hrworks/sui-core/BlockUI';
import { ConfirmationModal } from '@hrworks/sui-core/ConfirmationModal';
import { Grid } from '@hrworks/sui-core/Grid';
import { GridItem } from '@hrworks/sui-core/GridItem';
import { Text } from '@hrworks/sui-core/Text';

import { useIsCustomDomain } from '../../context/CustomDomainContext';
import { buildApplicationSchema } from '../../data-access/applicationSchema';
import { sendApplication } from '../../data-access/sendApplication';
import { resolveRoute } from '../../util/routeResolver';
import { captureException } from '../../util/sentryClient';
import { Card, CardBody, CardHeader } from '../Card';
import { Title } from '../Title';
import { S } from './ApplicationForm.styles';
import type { ApplicationFormProps, FormState } from './ApplicationForm.types';
import { ApplicationFormContext } from './ApplicationFormContext';
import { ApplicationFormFileManager } from './ApplicationFormFileManager';
import { fillTestData } from './debugAutoFill';
import { FormField } from './FormField';
import { NoticePeriodFields } from './NoticePeriodFields';
import { SubmitButton } from './SubmitButton';

// TODO: Remove custom type when OpenAPI spec includes 'position' in howDidYouHearAboutUs options
type HowDidYouHearAboutUsOption = {
  position?: number;
  de?: string | null;
  en?: string | null;
  [key: string]: string | number | null | undefined;
};

/** Textfelder aus dem FormData, damit sie nach einem Fehler wieder befuellt werden (wie im Server). */
const extractInputs = (formData: FormData): Record<string, string | undefined> =>
  Object.fromEntries(
    Array.from(formData.entries())
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      .map(([key, value]) => [key, value || undefined]),
  );

const initialState: FormState = {
  inputs: {},
  message: '',
  success: false,
  errors: {},
};

export const ApplicationForm = ({
  customerCompanyNumber,
  companyId,
  postOfferId,
  post,
  settings,
  ...otherProps
}: ApplicationFormProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const isCustom = useIsCustomDomain();
  const [formState, formAction] = useActionState(
    async (prevState: FormState, formData: FormData): Promise<FormState> => {
      try {
        return await sendApplication(customerCompanyNumber, postOfferId, prevState, formData);
      } catch (error) {
        // Die Server Action selbst kam nicht zustande (z. B. Proxy antwortet mit 503, Deploy
        // zwischen Laden und Absenden). Ohne catch ersetzt app/[locale]/error.tsx die ganze Seite
        // und die Eingaben sind weg; so bleibt das Formular mit demselben Alert wie bei einem
        // API-Fehler stehen.
        captureException(error);
        return { success: false, message: 'Submission request failed', errors: {}, inputs: extractInputs(formData) };
      }
    },
    initialState,
  );

  const [clientErrors, setClientErrors] = useState<Record<string, string[]>>({});
  const [fileCount, setFileCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const skipConfirmRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const formDataRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!formState.success) return;

    const routeCompanyId = isCustom ? undefined : companyId;
    const successUrl =
      resolveRoute('jobApplicationSuccess', { locale, companyId: routeCompanyId, id: postOfferId }) +
      Object.entries(formDataRef.current)
        .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`)
        .join('');
    if (isCustom) {
      window.location.href = successUrl;
    } else {
      router.push(successUrl);
    }
  }, [formState.success, companyId, postOfferId, router, isCustom, locale]);

  useEffect(() => {
    if (!formState.success && formState.message && isSubmitting) setIsSubmitting(false);
  }, [formState, isSubmitting]);

  // Reset loading state on server error
  useEffect(() => {
    const allErrs = { ...clientErrors, ...formState.errors };
    const firstKey = Object.keys(allErrs).find((k) => allErrs[k]?.length);
    if (!firstKey) return;
    const el = document.querySelector<HTMLElement>(`[name="${firstKey}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus();
  }, [clientErrors, formState.errors]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget);
    const schema = buildApplicationSchema(visibleFields as Record<string, string> | undefined);
    // Use ISO date from data-get-form-data-unmasked attribute (DateField stores ISO internally)
    const birthdayInput = e.currentTarget.querySelector<HTMLInputElement>('[name="birthday"]');
    if (birthdayInput?.dataset.getFormDataUnmasked) {
      formData.set('birthday', birthdayInput.dataset.getFormDataUnmasked);
    }
    // Use ISO date for earliestPossibleJoinDate (DateField stores ISO internally)
    const startDateInput = e.currentTarget.querySelector<HTMLInputElement>('[name="earliestPossibleJoinDate"]');
    if (startDateInput?.dataset.getFormDataUnmasked) {
      formData.set('earliestPossibleJoinDate', startDateInput.dataset.getFormDataUnmasked);
    }

    const result = schema.safeParse(Object.fromEntries(formData.entries()));

    const errors: Record<string, string[]> = {};

    if (!result.success) {
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        errors[key] = errors[key] || [];
        const emailValue = key === 'email' ? (formData.get('email') as string) || '' : '';
        const msg =
          issue.message === 'birthday-future'
            ? t('Validation.birthday-future' as Parameters<typeof t>[0])
            : issue.message === 'notice-period-max'
              ? t('Validation.notice-period-max' as Parameters<typeof t>[0])
              : issue.code === 'invalid_string' && key === 'email' && emailValue.length > 0
                ? t('Validation.invalid-mail' as Parameters<typeof t>[0])
                : t(`Validation.required-${key}` as Parameters<typeof t>[0]);
        errors[key].push(msg);
      }
    }

    if (howDidYouHearAboutUsConfiguration?.show && !formData.get('howDidYouHearAboutUs')) {
      errors.howDidYouHearAboutUs = [t('Validation.required-howDidYouHearAboutUs' as Parameters<typeof t>[0])];
    }

    // Explicit notice period validation (failsafe)
    if (formData.get('hasNoticePeriod') === 'on') {
      const amount = formData.get('noticePeriodTimeAmount');
      const unit = formData.get('noticePeriodTimeUnit');
      const reference = formData.get('noticePeriodTimeReference');
      if (!amount || amount === '0' || amount === '') {
        errors.noticePeriodTimeAmount = errors.noticePeriodTimeAmount || [];
        errors.noticePeriodTimeAmount.push(t('Validation.required-noticePeriodTimeAmount' as Parameters<typeof t>[0]));
      }
      if (!unit || unit === '') {
        errors.noticePeriodTimeUnit = errors.noticePeriodTimeUnit || [];
        errors.noticePeriodTimeUnit.push(t('Validation.required-noticePeriodTimeUnit' as Parameters<typeof t>[0]));
      }
      if (!reference || reference === '') {
        errors.noticePeriodTimeReference = errors.noticePeriodTimeReference || [];
        errors.noticePeriodTimeReference.push(
          t('Validation.required-noticePeriodTimeReference' as Parameters<typeof t>[0]),
        );
      }
    }

    // Max 2 years notice period validation
    if (formData.get('hasNoticePeriod') === 'on') {
      const amount = Number(formData.get('noticePeriodTimeAmount'));
      const unit = formData.get('noticePeriodTimeUnit') as string;
      const maxByUnit: Record<string, number> = { days: 730, weeks: 104, months: 24 };
      const max = maxByUnit[unit] || 730;
      if (amount > max) {
        errors.noticePeriodTimeAmount = errors.noticePeriodTimeAmount || [];
        errors.noticePeriodTimeAmount.push(t('Validation.notice-period-max' as Parameters<typeof t>[0]));
      }
    }

    if (fileCount === 0) {
      errors.files = [t('Validation.required-files' as Parameters<typeof t>[0])];
    }

    if (Object.keys(errors).length > 0) {
      e.preventDefault();
      setClientErrors(errors);

      return;
    }

    const fields = ['email', 'firstName', 'lastName', 'salutation', 'title'] as const;
    formDataRef.current = Object.fromEntries(
      fields.map((f) => [f, (formData.get(f) as string) || '']).filter(([, v]) => v),
    );
    // Ensure file input has name so files are included in FormData
    const fileInput = e.currentTarget.querySelector<HTMLInputElement>('[data-file-manager-id]');
    if (fileInput) fileInput.name = 'files';
    setClientErrors({});
    if (!skipConfirmRef.current) {
      e.preventDefault();
      setShowConfirmModal(true);
      return;
    }
    skipConfirmRef.current = false;
    setIsSubmitting(true);
  };

  const handleConfirmSubmit = () => {
    setShowConfirmModal(false);
    skipConfirmRef.current = true;
    formRef.current?.requestSubmit();
  };

  const allErrors = { ...clientErrors, ...formState.errors };
  const selectNoneOption = { label: t('Form.please-select'), value: '' };

  const salutationOptions = [
    selectNoneOption,
    { label: t('FormFields.salutation-options.mr'), value: 'mr' },
    { label: t('FormFields.salutation-options.ms'), value: 'ms' },
    { label: t('FormFields.salutation-options.none'), value: 'none' },
  ];

  const { visibleFields, postOffer } = post;
  const { howDidYouHearAboutUsConfiguration, privacyTermsUrl, privacyConsentTexts } = settings;
  // Mandantenspezifische Consent-Texte aus den Jobportal-Einstellungen; Fallback (undefined)
  // ist der statische Text aus messages/*.json.
  const getConsentText = (key: 'privacyPolicyLink' | 'talentpool') =>
    locale === 'de' ? privacyConsentTexts?.[key]?.de : privacyConsentTexts?.[key]?.en;
  const privacyConsentLabel = getConsentText('privacyPolicyLink');
  const talentpoolConsentLabel = getConsentText('talentpool');

  const howDidYouHearAboutUsOptions = (() => {
    const raw = howDidYouHearAboutUsConfiguration?.options;
    if (!raw) return [selectNoneOption];

    // Format 1 (Schema): { de: string[], en: string[] } — arrays per language
    if (Array.isArray(raw.de) || Array.isArray(raw.en)) {
      const localizedArray =
        (raw as { de?: string[]; en?: string[] })[locale as 'de' | 'en'] || (raw as { de?: string[] }).de || [];
      return [
        selectNoneOption,
        ...localizedArray.map((label: string, idx: number) => ({ label, value: label || String(idx) })),
      ];
    }

    // Format 2 (Legacy/Object): { key: { de: string, en: string, position?: number } }
    return [
      selectNoneOption,
      ...Object.entries(raw)
        .filter(([, translations]) => {
          // Filter out entries with all-null translations (broken custom channels)
          const trans = translations as unknown as HowDidYouHearAboutUsOption | undefined;
          if (!trans) return false;
          return Object.values(trans).some((v) => v !== null && v !== undefined && v !== '');
        })
        .sort(([, a], [, b]) => {
          const posA = (a as unknown as HowDidYouHearAboutUsOption)?.position ?? Number.MAX_SAFE_INTEGER;
          const posB = (b as unknown as HowDidYouHearAboutUsOption)?.position ?? Number.MAX_SAFE_INTEGER;
          return posA - posB;
        })
        .map(([value, translations]) => {
          const trans = translations as unknown as HowDidYouHearAboutUsOption | undefined;
          const label =
            trans?.[locale as 'de' | 'en'] ||
            (trans ? Object.values(trans).find((v) => typeof v === 'string' && v) : undefined) ||
            value;
          return { label: String(label), value };
        }),
    ];
  })();

  return (
    <>
      <BlockUI isOpen={isSubmitting} />
      <ApplicationFormContext.Provider value={{ formState: { ...formState, errors: allErrors } }}>
        <S.Container data-guide-id="application-form" {...otherProps}>
          <form ref={formRef} action={formAction} onSubmit={handleSubmit}>
            {process.env.NODE_ENV === 'development' && (
              <button
                type="button"
                onClick={() => formRef.current && fillTestData(formRef.current)}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 999,
                  padding: '4px 8px',
                  fontSize: '11px',
                  background: '#ff9f6c',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  opacity: 0.9,
                }}
              >
                🧪 Fill Test Data
              </button>
            )}
            <Card>
              <CardHeader>
                <Grid gap="8px">
                  <GridItem>
                    <Title typography="headingLg">{t('Main.application-form')}</Title>
                  </GridItem>
                  <GridItem>
                    <Title>{postOffer.displayName}</Title>
                  </GridItem>
                </Grid>
              </CardHeader>
              <CardBody>
                <Text>{`*${t('Form.required-fields')}`}</Text>
                <S.FormFieldContainer>
                  <FormField
                    fieldKey="salutation"
                    options={salutationOptions}
                    component="Select"
                    visibility="Mandatory"
                  />
                  <FormField fieldKey="title" />
                  <FormField fieldKey="firstName" visibility="Mandatory" />
                  <FormField fieldKey="lastName" visibility="Mandatory" />
                  <FormField fieldKey="email" visibility="Mandatory" />
                  {visibleFields && (
                    <>
                      <FormField fieldKey="phone" visibility={visibleFields.mobilePhone} component="PhoneField" />
                      <FormField fieldKey="street" visibility={visibleFields.address} />
                      <FormField fieldKey="streetNumber" visibility={visibleFields.address} />
                      <FormField
                        fieldKey="additional"
                        visibility={visibleFields.address === 'Mandatory' ? 'Applicant' : visibleFields.address}
                      />
                      <FormField fieldKey="countryCode" visibility={visibleFields.address} component="CountrySelect" />
                      <FormField fieldKey="zipCode" visibility={visibleFields.address} />
                      <FormField fieldKey="city" visibility={visibleFields.address} />
                      <FormField fieldKey="birthday" visibility={visibleFields.birthday} component="DateField" />
                      <FormField fieldKey="birthplace" visibility={visibleFields.birthPlace} />
                      <FormField fieldKey="fieldOfStudy" visibility={visibleFields.fieldOfStudy} />
                      {(visibleFields.lastAnnualSalary === 'Applicant' ||
                        visibleFields.lastAnnualSalary === 'Mandatory') && (
                        <GridItem size={12}>
                          <Grid>
                            <FormField
                              fieldKey="lastAnnualSalary"
                              visibility={visibleFields.lastAnnualSalary}
                              component="NumberInputField"
                              min={0}
                              signed={false}
                            />
                            <FormField
                              fieldKey="lastAnnualSalaryCurrency"
                              visibility={visibleFields.lastAnnualSalary}
                              component="CurrencySelect"
                              defaultValue="EUR"
                            />
                          </Grid>
                        </GridItem>
                      )}
                      <FormField
                        fieldKey="desiredSalary"
                        visibility={visibleFields.desiredSalary}
                        component="NumberInputField"
                        min={0}
                        signed={false}
                      />
                      <FormField
                        fieldKey="earliestPossibleJoinDate"
                        component="DateField"
                        visibility={visibleFields.earliestPossibleJoinDate}
                      />
                      <NoticePeriodFields visibility={visibleFields.noticePeriod} />
                      <FormField
                        fieldKey="isSeverelyDisabled"
                        visibility={
                          visibleFields.severelyDisabled === 'NotVisible' ||
                          visibleFields.severelyDisabled === 'Administrator'
                            ? visibleFields.severelyDisabled
                            : 'Visible'
                        }
                        component="Checkbox"
                      />
                      {howDidYouHearAboutUsConfiguration?.show && (
                        <FormField
                          fieldKey="howDidYouHearAboutUs"
                          visibility="Mandatory"
                          component="Select"
                          options={howDidYouHearAboutUsOptions}
                        />
                      )}
                      <FormField fieldKey="remark" size={12} visibility={visibleFields.remark} component="Textarea" />
                      <ApplicationFormFileManager onFileCountChange={setFileCount} />
                    </>
                  )}
                  {/* Immer aktiv – die API liefert dafür bewusst keinen visibleFields-Eintrag. */}
                  <FormField
                    fieldKey="permissionToStoreApplication"
                    component="Checkbox"
                    visibility="Applicant"
                    label={talentpoolConsentLabel}
                    size={12}
                  />
                  <FormField
                    fieldKey="mayBePassedWithinCorporateGroup"
                    visibility={visibleFields?.mayBePassedWithinCorporateGroup}
                    component="Checkbox"
                    size={12}
                  />
                  {privacyTermsUrl && (
                    <GridItem>
                      <S.PrivacyLink data-guide-id="form-privacy-link" href={privacyTermsUrl} target="_blank">
                        {privacyConsentLabel || t('Form.privacy-policy-read-more')}
                      </S.PrivacyLink>
                    </GridItem>
                  )}
                  {formState.message && !formState.success && (
                    <GridItem size={12}>
                      <Alert color="danger">{t('Form.submission-error')}</Alert>
                    </GridItem>
                  )}
                </S.FormFieldContainer>
              </CardBody>
            </Card>
            <S.ButtonWrapper>
              <SubmitButton size="large" />
            </S.ButtonWrapper>
          </form>
        </S.Container>
      </ApplicationFormContext.Provider>
      <ConfirmationModal
        show={showConfirmModal}
        title={t('Form.confirm-submit-title' as Parameters<typeof t>[0])}
        onConfirm={handleConfirmSubmit}
        confirmText={t('Form.confirm-yes' as Parameters<typeof t>[0])}
        onCancel={() => setShowConfirmModal(false)}
        cancelText={t('Form.confirm-no' as Parameters<typeof t>[0])}
        data-guide-id="form-confirmation-modal"
        slotProps={{
          cancel: { 'data-guide-id': 'form-confirmation-modal-cancel-button' },
          confirm: { 'data-guide-id': 'form-confirmation-modal-confirm-button' },
        }}
      >
        <div data-guide-id="form-confirmation-modal-body">
          <p>{t('Form.confirm-submit-intro' as Parameters<typeof t>[0])}</p>
          <p>
            <strong>E-Mail:</strong> {formDataRef.current.email || ''}
          </p>
          <p>{t('Form.confirm-submit-question' as Parameters<typeof t>[0])}</p>
        </div>
      </ConfirmationModal>
    </>
  );
};
